import {ChatbotEnv} from '@/types/chatbotEnv';
import {systemPromptService} from './SystemPromptService';
import {fissionChatbotService} from './FissionChatbotService';

/**
 * Interface for LLM request parameters
 */
interface LLMRequestParams {
    userMessage: string;
    userId: string;
    chatWrapperId: string;
    environment: ChatbotEnv;
    businessRole?: string;
    isFastmodeActive?: boolean;
}

/**
 * Interface for LLM response
 */
interface LLMResponse {
    content: string;
    metadata?: any;
}

/**
 * Service for managing LLM message requests and responses
 * Orchestrates the entire request flow using other specialized services
 */
export class MessageManagerService {
    /**
     * Sends a request to the LLM and returns the formatted response
     * @param params Request parameters including user message, chat history, environment, and business role
     * @returns Promise resolving to the LLM response
     */
    public async sendLLMRequest(params: LLMRequestParams): Promise<LLMResponse> {
        const {userMessage, userId, chatWrapperId, environment, businessRole, isFastmodeActive} = params;

        console.log('[TEST] MessageManagerService - Starting LLM request process');
        console.log('[TEST] MessageManagerService - Chat wrapper ID:', chatWrapperId);
        console.log('[TEST] MessageManagerService - Environment:', environment);
        console.log('[TEST] MessageManagerService - Business role:', businessRole || 'Not specified');
        console.log('[TEST] MessageManagerService - FastMode active:', isFastmodeActive !== undefined ? isFastmodeActive : true);

        try {
            console.log('[TEST] MessageManagerService - Sending request to Fission chatbot service');
            
            // Send the request to the Fission function
            const response = await fissionChatbotService.sendRequest({
                userMessage,
                environment,
                businessRole,
                userId,
                conversationId: chatWrapperId,
                isFastmodeActive,
                isPresentationMode: false,
            });

            console.log('[TEST] MessageManagerService - Received response from Fission chatbot service');
            console.log('[TEST] MessageManagerService - Response will be returned to useChat but NOT saved by frontend');

            const message = {
                content: response.content,
                metadata: response.metadata
            }

            console.log("Returned Message: ", message);
            // Return the formatted response
            return message;
        } catch (error) {
            console.error('Error in sendLLMRequest:', error);
            console.error('Returning error message:');

            // Return a graceful error message formatted as HTML
            return {
                content: `
                    <div class="error-message">
                        <h3 class="text-red-600">Error Processing Request</h3>
                        <p>I'm sorry, but I encountered an error while processing your request. Please try again later.</p>
                        <p class="text-sm text-gray-500">Error details: ${error instanceof Error ? error.message : 'Unknown error'}</p>
                    </div>
                    `,
                metadata: null,
            };
        }
    }

    /**
     * Updates the Fission function URL
     * @param fissionUrl The new Fission function URL
     */
    public updateFissionUrl(fissionUrl: string): void {
        fissionChatbotService.updateFissionUrl(fissionUrl);
    }

    /**
     * Updates or adds a system prompt for a specific environment and business role
     * @param environment The chatbot environment
     * @param businessRole The user's business role, or 'default' for the default prompt
     * @param promptText The specific instructions for this prompt
     */
    public updateSystemPrompt(environment: ChatbotEnv, businessRole: string, promptText: string): void {
        systemPromptService.setSystemPrompt(environment, businessRole, promptText);
    }
}

// Export a singleton instance
export const messageManagerService = new MessageManagerService();